import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API from "../lib/api";
import PlatformBrand from "../components/PlatformBrand";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { MapPin, CurrencyGbp, CheckCircle, XCircle, Clock, File as FileIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";

export default function ClientApprovalPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [responded, setResponded] = useState(false);

  useEffect(() => {
    API.get(`/job-requests/client-approval/${token}`)
      .then(({ data }) => {
        setData(data);
        if (data.used) setResponded(true);
      })
      .catch(err => setError(err.response?.data?.detail || "Invalid or expired link"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRespond = async (decision) => {
    setSubmitting(true);
    try {
      await API.post(`/job-requests/client-approval/${token}/respond`, { decision, notes: notes.trim() });
      setResponded(true);
      toast.success(decision === "approve" ? "Quote approved! The team will be in touch." : "Quote declined.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit response");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
      <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
      <div className="text-center max-w-md">
        <XCircle size={48} className="mx-auto text-red-400 mb-4" />
        <h1 className="text-xl font-bold mb-2">Link Unavailable</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    </div>
  );

  const q = data?.selected_quote;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="client-approval-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="client-approval-brand">
            {data?.branding?.logo_url ? <img src={data.branding.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${data.branding.logo_url}` : data.branding.logo_url} alt={data.branding.company_name || "Logo"} className="h-8 w-auto max-w-[140px] object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <PlatformBrand variant="icon" />}
            <div>
              <h2 className="text-base font-bold font-[Chivo] text-slate-900 dark:text-white leading-none">{data?.branding?.company_name || "Fixlr"}</h2>
              <p className="text-[10px] text-muted-foreground">{data?.branding?.tagline || "Quote approval"}</p>
            </div>
          </div>
          <span className="text-xs font-mono text-muted-foreground">{data?.reference}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {responded ? (
          <div className="text-center py-16" data-testid="approval-responded">
            <CheckCircle size={56} className="mx-auto text-emerald-500 mb-4" weight="duotone" />
            <h1 className="text-2xl font-bold font-[Chivo] mb-2">Thank You</h1>
            <p className="text-muted-foreground">Your response has been recorded. The team will be in touch shortly.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold font-[Chivo] mb-1" data-testid="approval-title">Quote for Your Approval</h1>
            <p className="text-sm text-muted-foreground mb-6">Hi {data?.client_name}, please review the details below and approve or decline.</p>

            {/* Job details */}
            <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5 mb-4" data-testid="approval-job-details">
              <h2 className="text-lg font-semibold mb-3">{data?.title}{data?.parent_job_reference && <span className="block text-xs font-normal text-muted-foreground mt-0.5" data-testid="approval-parent-job">Part of main job {data.parent_job_reference}</span>}</h2>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                {data?.property_address && (
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-muted-foreground shrink-0 mt-0.5" />
                    <span>{data.property_address}</span>
                  </div>
                )}
                {data?.category && (
                  <div className="text-muted-foreground">
                    Category: <span className="font-medium text-foreground">{data.category.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </div>
                )}
                {data?.requested_completion_date && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock size={16} className="shrink-0" />
                    Target: {fmtDate(data.requested_completion_date)}
                  </div>
                )}
              </div>
              {data?.request_details && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">SCOPE OF WORK</p>
                  <p className="text-sm whitespace-pre-wrap">{data.request_details}</p>
                </div>
              )}
            </div>

            {/* Selected quote */}
            {q && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-5 mb-4" data-testid="approval-quote">
                <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-2">{q.is_client_price ? "QUOTE TOTAL" : "RECOMMENDED QUOTE"}</p>
                <div className="flex items-baseline justify-between mb-2">
                  <p className="text-sm font-medium">{q.vendor_name}</p>
                  <p className="text-2xl font-bold font-[Chivo] flex items-center" data-testid="approval-quote-amount"><CurrencyGbp size={20} />{q.amount?.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{q.is_client_price && <span className="text-xs font-normal text-muted-foreground ml-1">ex VAT</span>}</p>
                </div>
                {q.description && <p className="text-sm text-muted-foreground mb-2">{q.description}</p>}
                {q.validity_date && <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> Valid until: {q.validity_date}</p>}
              </div>
            )}

            {/* Attachments */}
            {data?.attachments?.length > 0 && (
              <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5 mb-6">
                <p className="text-xs font-semibold text-muted-foreground mb-2">ATTACHMENTS</p>
                <div className="flex flex-wrap gap-2">
                  {data.attachments.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-muted px-3 py-2 rounded text-xs hover:bg-muted/80">
                      {a.type?.startsWith("image/") ? (
                        <img src={a.url} alt={a.name} className="h-8 w-8 rounded object-cover" />
                      ) : (
                        <FileIcon size={14} className="text-muted-foreground" />
                      )}
                      <span>{a.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Response section */}
            <div className="bg-white dark:bg-slate-900 border border-border rounded-lg p-5" data-testid="approval-response-section">
              <p className="text-sm font-semibold mb-3">Your Response</p>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Optional: Add any notes or questions..."
                rows={3}
                className="mb-4"
                data-testid="approval-notes-input"
              />
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11"
                  onClick={() => handleRespond("approve")}
                  disabled={submitting}
                  data-testid="approval-approve-btn"
                >
                  <CheckCircle size={18} className="mr-2" weight="bold" />
                  {submitting ? "Submitting..." : "Approve Quote"}
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => handleRespond("decline")}
                  disabled={submitting}
                  data-testid="approval-decline-btn"
                >
                  <XCircle size={18} className="mr-2" />
                  Decline
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
